import { create } from 'zustand';

import { WizardOptionCondition } from '../../tokens';
import { WizardItem } from '../../types/wizard';

import { WizardState } from './Wizard.store.types';

let foundStep: WizardItem;
let foundResults: WizardItem[];

const useWizardStore = create<WizardState>((set, get) => ({
  items: [],
  steps: [],
  results: [],
  stepIndex: -1,
  initialize: (data) =>
    set(() => ({ stepIndex: 0, items: data, steps: deepClone(data) })),
  reset: () =>
    set((state) => ({
      steps: deepClone(state.items),
      results: [],
      stepIndex: 0,
    })),
  getItem: (id) => findStep(get().steps, id),
  updateItem: (itemID, parentItemID, isChecked, itemTextValue) =>
    set((state) => {
      foundResults = [];

      // The parentOptionID is passed when the wizard option is grouped.
      if (parentItemID) {
        const parentStep = findStep(state.steps, parentItemID);

        // Uncheck other values in the radio group before selecting current option.
        parentStep.items = parentStep.items.map((item) => ({
          ...item,
          value: item.id === itemID ? isChecked : false,
        }));
      }

      const steps = getUpdatedSteps(
        state.steps,
        state.stepIndex,
        itemID,
        isChecked,
        !!parentItemID,
        itemTextValue
      );
      const results = getResults(steps);

      // Set the updated steps and results by returning them to the store.
      return {
        steps,
        results,
      };
    }),
  setStepIndex: (index: number) =>
    set((state) => {
      const stepIndex = index >= 0 ? Math.min(index, state.steps.length) : 0;

      return { stepIndex };
    }),
  nextStepIndex: () =>
    set((state) => {
      window.scrollTo(0, 0);

      return { stepIndex: Math.min(state.stepIndex + 1, state.steps.length) };
    }),
  previousStepIndex: () =>
    set((state) => ({ stepIndex: Math.max(0, state.stepIndex - 1) })),
  lastStepIndex: () => set((state) => ({ stepIndex: state.steps.length })),
}));

const getUpdatedSteps = (
  steps: WizardState['steps'],
  stepIndex: number,
  stepID: string,
  isChecked: boolean,
  itemsAreGrouped: boolean,
  itemTextValue?: string
): WizardState['steps'] => {
  const step = findStep(steps, stepID);
  const subStep = steps.find((item) => item.id === `${stepIndex}`);

  // Set active value on the step.
  step.value = isChecked;

  // If there is a text value update, apply to the step.
  step.textValue = itemTextValue;

  // When a step value is selected, but it doesn't have items and it is inside a group. Remove the subStep(s) entirely.
  if (step.value && !step.items.length && subStep && itemsAreGrouped) {
    return pruneSteps(steps, stepIndex);
  }

  /* When a step value is selected, check if there are items. 
  If there are items, append them to the substep if there is one. Else create the substep at the current stepIndex + 1. */
  if (step.value && step.items.length) {
    if (subStep) {
      if (itemsAreGrouped) {
        subStep.items = [...step.items];
      } else {
        subStep.items = [...subStep.items, ...step.items];
      }
    } else {
      // Append the newly created step in the main steps array.
      const currentStep = steps[stepIndex];
      const subStepIndex = stepIndex + 1;

      steps.splice(subStepIndex, 0, {
        id: `${stepIndex}`,
        items: [...step.items],
        tracking: {
          name: `${currentStep.tracking.name.split(' |')[0]} | extra step ${
            currentStep.tracking.mainStep + 1
          }`,
          mainStep: currentStep.tracking.mainStep,
          subStep: currentStep.tracking.subStep + 1,
        },
      });
    }
  }

  /* If a step value is false and it has items, make sure to remove it from the items of the active substep.
  In case the substep items become empty, remove the substep entirely. */
  if (!step.value && step.items.length) {
    if (subStep) {
      const items = subStep.items.filter(
        (subStepItem) =>
          !!step.items.filter((item) => item.id !== subStepItem.id).length
      );

      if (items.length) {
        subStep.items = items;
      } else {
        return pruneSteps(steps, stepIndex);
      }
    } else {
      return steps.filter((item) => item.id !== stepID);
    }
  }

  return steps;
};

// Find a wizard item in all steps, recursively
const findStep = (
  steps: WizardState['steps'],
  stepID: WizardItem['id']
): WizardItem => {
  steps.forEach((step) => {
    if (step.id === stepID) {
      foundStep = step;
    }

    return step.items && findStep(step.items, stepID);
  });

  return foundStep;
};

/* The prune steps method removes the step with the id of the current step. 
If there are more steps created based on the stepindex, also remove these. */
const pruneSteps = (
  steps: WizardState['steps'],
  stepIndex: number
): WizardState['steps'] =>
  steps.filter((item, index) => {
    if (
      item.id === `${stepIndex}` ||
      (`${item.tracking.mainStep}` === `${stepIndex}` && index > stepIndex)
    ) {
      return false;
    }

    return true;
  });

// Returns a unique array with results
const getResults = (steps: WizardState['steps']): WizardItem[] => {
  const results = findResults(steps).reduce(
    (accumulator: WizardItem[], step: WizardItem) => {
      const isDuplicateResultCard = !!accumulator.find(
        (item) => item.contentfulId === step.contentfulId
      );

      return [...accumulator, ...[!isDuplicateResultCard && step]].filter(
        Boolean
      ) as WizardItem[];
    },
    []
  );

  /* When step condition SKIP_STEPS is selected in the CMS, only return the option where SKIP_STEPS condition is set.
    For example in a scenario where a "Not applicable" card is placed, we don't want to show other result cards. */
  const skipResults = results.filter(
    (result) => result.condition === WizardOptionCondition.SKIP_STEPS
  );

  return skipResults.length ? skipResults : results;
};

// Find results in all steps, recursively
const findResults = (
  steps: WizardState['steps'],
  parentId?: WizardItem['id']
): WizardItem[] => {
  steps.forEach((step) => {
    if (
      step.value &&
      (step.visual?.results || step.visual?.resultTags || step.textValue)
    ) {
      foundResults = [...(foundResults || []), { ...step, parentId }];
    }

    return step.items && findResults(step.items, step.id);
  });

  return foundResults;
};

// Retuns a deep clone of the items passed. Used to be able to reset the store state.
const deepClone = (data: WizardState['items']): WizardState['items'] =>
  JSON.parse(JSON.stringify(data));

export { useWizardStore };
